/** The AI models list state: search, filters, sort and paging, all in the browser. */

import { useMemo, useState } from 'react';

import type { AiModel } from '@/api/types';
import { useClientPaging } from '@/ui/useClientPaging';

import { filterModels, nextSort, sortModels, type ModelSort, type SortKey } from './modelList';

export function useModelList(rows: AiModel[], hostingLabel: Record<string, string>) {
  const [search, setSearch] = useState('');
  const [providers, setProviders] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [sort, setSort] = useState<ModelSort>({ key: 'display_name', order: 'asc' });

  const visible = useMemo(
    () => sortModels(filterModels(rows, { search, providers, statuses }), sort, hostingLabel),
    [rows, search, providers, statuses, sort, hostingLabel],
  );

  const { page, setPage, pageSize, setPageSize, pageRows } = useClientPaging(visible, [search, providers, statuses, sort], 25);

  function onSort(key: string) {
    setSort((s) => nextSort(s, key as SortKey));
  }

  return {
    search,
    setSearch,
    providers,
    setProviders,
    statuses,
    setStatuses,
    sort,
    onSort,
    page,
    setPage,
    pageSize,
    setPageSize,
    visible,
    pageRows,
  };
}

export type ModelList = ReturnType<typeof useModelList>;
